/**
 * `VersionPage` — GVW-156 version / build info.
 *
 * Per brief §8: real `<dl>` definition list with mono build hash and a
 * "Copy build hash" `<button>`. Reads `import.meta.env.VITE_GIT_SHA`,
 * `VITE_BUILD_TIME`, `VITE_APP_VERSION` per dispatch §4.4.h. Shows
 * design-system bridge state per boss §14.1 / brief §16.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-156/brief.md
 */

import { useCallback, useEffect, useState } from 'react';
import { tokens } from '@/design';
import { useDefenceVariant } from '@/design/hooks';
import { useLiveRegion } from '@/state/liveRegion';

interface BuildInfo {
  app: string;
  version: string;
  build: string;
  buildTime: string;
  designSystem: string;
  variant: 'standard' | 'defence';
  sdkBridge: 'documented-bridge' | 'published';
}

function readBuildInfo(variant: 'standard' | 'defence'): BuildInfo {
  // Vite `import.meta.env` access. If a key is missing at build time it
  // becomes `undefined`; we fall back to "unavailable" per dispatch §4.4.h.
  const env = (typeof import.meta !== 'undefined' && import.meta && (import.meta as { env?: Record<string, string | undefined> }).env) || {};
  return {
    app: 'GeoViewer',
    version: env.VITE_APP_VERSION ?? '2026.2.0-phase2',
    build: env.VITE_GIT_SHA ?? 'unavailable',
    buildTime: env.VITE_BUILD_TIME ?? 'unavailable',
    designSystem: 'documented-bridge', // boss §14.1; remains until RFC-104 P7 swap
    variant,
    sdkBridge: 'documented-bridge',
  };
}

export function VersionPage() {
  const liveRegion = useLiveRegion();
  const defenceVariant = useDefenceVariant();
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setBuildInfo(readBuildInfo(defenceVariant.active ? 'defence' : 'standard'));
  }, [defenceVariant.active]);

  useEffect(() => {
    document.title = 'Version · GeoViewer';
    liveRegion.announce('status', 'Version information loaded.');
  }, [liveRegion]);

  const handleCopy = useCallback(async () => {
    if (!buildInfo || buildInfo.build === 'unavailable') return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(buildInfo.build);
      }
      setCopied(true);
      liveRegion.announce('status', 'Build hash copied to clipboard.');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      liveRegion.announce('alert', 'Could not copy build hash.');
    }
  }, [buildInfo, liveRegion]);

  if (!buildInfo) {
    return (
      <main
        role="main"
        aria-labelledby="gvw-156-heading"
        data-gvw-id="GVW-156"
        data-route="/workspace/help/version"
        style={{
          minHeight: '100%',
          background: tokens.color.surface[0],
          color: tokens.color.ink.primary,
          fontFamily: tokens.font.family.ui,
          padding: tokens.space[5],
          boxSizing: 'border-box',
        }}
      >
        <h1
          id="gvw-156-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            fontWeight: tokens.font.weight.semibold,
          }}
        >
          Version information
        </h1>
        <p style={{ color: tokens.color.ink.secondary }}>Loading…</p>
      </main>
    );
  }

  return (
    <main
      role="main"
      aria-labelledby="gvw-156-heading"
      data-gvw-id="GVW-156"
      data-route="/workspace/help/version"
      style={{
        minHeight: '100%',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <h1
        id="gvw-156-heading"
        style={{
          margin: 0,
          marginBottom: tokens.space[4],
          fontSize: tokens.font.size['2xl'],
          fontWeight: tokens.font.weight.semibold,
          display: 'flex',
          alignItems: 'center',
          gap: tokens.space[3],
        }}
      >
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 28 }}>
          info
        </span>
        Version information
      </h1>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: tokens.space[5],
          rowGap: tokens.space[3],
          maxWidth: 720,
          padding: tokens.space[4],
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderRadius: tokens.radius.md,
          margin: 0,
        }}
      >
        <dt style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>Application</dt>
        <dd style={{ margin: 0, fontWeight: tokens.font.weight.medium }}>{buildInfo.app}</dd>

        <dt style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>Version</dt>
        <dd style={{ margin: 0 }}>{buildInfo.version}</dd>

        <dt style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>Build hash</dt>
        <dd style={{ margin: 0, display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
          <code
            style={{
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.role.engMono.size,
              padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
              background: tokens.color.surface[2],
              border: tokens.border.thin,
              borderRadius: tokens.radius.chip,
              userSelect: 'all',
            }}
          >
            {buildInfo.build}
          </code>
          {buildInfo.build !== 'unavailable' ? (
            <button
              type="button"
              aria-label="Copy build hash"
              onClick={handleCopy}
              style={{
                padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                background: tokens.color.surface[2],
                color: tokens.color.ink.primary,
                border: tokens.border.thin,
                borderRadius: tokens.radius.sm,
                cursor: 'pointer',
                fontSize: tokens.font.size.sm,
                display: 'inline-flex',
                alignItems: 'center',
                gap: tokens.space[1],
              }}
            >
              <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 14 }}>
                {copied ? 'check' : 'content_copy'}
              </span>
              {copied ? 'Copied' : 'Copy'}
            </button>
          ) : null}
        </dd>

        <dt style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>Build time</dt>
        <dd style={{ margin: 0 }}>{buildInfo.buildTime}</dd>

        <dt style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>Design system</dt>
        <dd style={{ margin: 0, display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
          {buildInfo.designSystem}
          <span
            style={{
              fontSize: tokens.font.size.xs,
              padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
              background: tokens.color.surface[2],
              color: tokens.color.ink.secondary,
              borderRadius: tokens.radius.chip,
            }}
          >
            {buildInfo.sdkBridge}
          </span>
        </dd>

        <dt style={{ color: tokens.color.ink.secondary, fontSize: tokens.font.size.sm }}>Variant</dt>
        <dd style={{ margin: 0, display: 'flex', alignItems: 'center', gap: tokens.space[2] }}>
          {buildInfo.variant}
          {buildInfo.variant === 'defence' ? (
            <span
              style={{
                fontSize: tokens.font.size.xs,
                padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                background: tokens.color.surface[2],
                color: tokens.color.semantic.warning,
                borderRadius: tokens.radius.chip,
                display: 'inline-flex',
                alignItems: 'center',
                gap: tokens.space[1],
              }}
            >
              <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 12 }}>
                security
              </span>
              Active
            </span>
          ) : null}
        </dd>
      </dl>

      <p style={{ marginTop: tokens.space[5], color: tokens.color.ink.muted, fontSize: tokens.font.size.sm }}>
        <span aria-hidden="true" style={{ fontFamily: 'Material Symbols Outlined', fontSize: 14, marginRight: tokens.space[1], verticalAlign: 'middle' }}>
          new_releases
        </span>
        Looking for what's new? See <a href="/workspace/help/docs" style={{ color: tokens.color.accent.primary }}>Documentation</a>.
      </p>
    </main>
  );
}
